import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const DISMISS_KEY = 've_sticky_cta_dismissed';

/** Drives the mobile-only sticky conversion bar (see MobileStickyCta.js):
 * visible once the visitor has scrolled past the fold (the homepage hero,
 * where one exists, else roughly one screen height), hidden again once the
 * footer comes into view so it never covers the footer's own content, and
 * permanently hidden for the rest of this browser tab's session once
 * dismissed. Desktop never sees it — the hook simply never turns `visible`
 * true there. */
export default function useMobileStickyCta() {
  const location = useLocation();
  const [pastFold, setPastFold] = useState(false);
  const [nearFooter, setNearFooter] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  const ticking = useRef(false);

  const dismiss = useCallback(() => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, '1');
  }, []);

  useEffect(() => {
    setPastFold(false);
    setNearFooter(false);

    const mq = window.matchMedia('(max-width: 767px)');
    if (!mq.matches) return undefined;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const hero = document.querySelector('.hero-main');
        const foldY = hero
          ? hero.getBoundingClientRect().bottom + window.scrollY
          : window.innerHeight * 0.6;
        setPastFold(window.scrollY > foldY - 80);
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    let footerObserver;
    const footer = document.getElementById('site-footer');
    if (footer) {
      footerObserver = new IntersectionObserver(
        ([entry]) => setNearFooter(entry.isIntersecting),
        { rootMargin: '0px 0px -1% 0px' }
      );
      footerObserver.observe(footer);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (footerObserver) footerObserver.disconnect();
    };
  }, [location.pathname]);

  return { visible: pastFold && !nearFooter && !dismissed, dismiss };
}
